import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";
import { queryKeys } from "../../lib/query-keys";
import type { CompiledRoutineProcedure, Routine, RoutineRunOutcome } from "../../lib/types";

/**
 * Sprint 14 — Routine hooks. Routines are declared recurring duties; Kuvio never
 * executes them. These hooks just read/write the declarations and the host's
 * run-back telemetry via the MCP tools.
 */

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["agent-routines"] });
  qc.invalidateQueries({ queryKey: ["routine"] });
}

function unwrap<T>(result: { isError?: boolean; structuredContent?: T; content?: Array<{ text?: string }> }): T {
  if (result.isError) {
    throw new Error(result.content?.[0]?.text ?? "Request failed");
  }
  return result.structuredContent as T;
}

export function useAgentRoutines(agentId: string | undefined, enabled?: boolean, includeArchived?: boolean) {
  return useQuery<Routine[]>({
    queryKey: agentId ? [...queryKeys.agentRoutines(agentId, enabled), includeArchived ?? false] : ["agent-routines", ""],
    queryFn: async () => {
      if (!agentId) return [];
      const r = await mcpClient.toolsCall<{ routines: Routine[] }>("list_agent_routines", {
        agent_id: agentId,
        ...(enabled === undefined ? {} : { enabled }),
        ...(includeArchived ? { include_archived: true } : {}),
      });
      return unwrap(r)?.routines ?? [];
    },
    enabled: !!agentId,
    staleTime: 15_000,
  });
}

export function useRoutine(id: string | undefined) {
  return useQuery<Routine | null>({
    queryKey: id ? queryKeys.routine(id) : ["routine", ""],
    queryFn: async () => {
      if (!id) return null;
      const r = await mcpClient.toolsCall<{ routine: Routine }>("get_routine", { id });
      return unwrap(r)?.routine ?? null;
    },
    enabled: !!id,
    staleTime: 15_000,
  });
}

export interface CreateRoutineParams {
  agent_id: string;
  name: string;
  purpose: string;
  cadence: string;
  prompt_body?: string;
  prompt_ref?: string;
  completion_criteria?: string;
  expected_outputs?: string[];
  enabled?: boolean;
}

export function useCreateRoutine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: CreateRoutineParams): Promise<Routine> => {
      const r = await mcpClient.toolsCall<{ routine: Routine }>("create_routine", params as unknown as Record<string, unknown>);
      return unwrap(r).routine;
    },
    onSuccess: () => invalidate(qc),
  });
}

export interface UpdateRoutineParams {
  id: string;
  name?: string;
  purpose?: string;
  cadence?: string;
  prompt_body?: string;
  prompt_ref?: string;
  completion_criteria?: string;
  expected_outputs?: string[];
  enabled?: boolean;
}

export function useUpdateRoutine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...fields }: UpdateRoutineParams): Promise<Routine> => {
      const r = await mcpClient.toolsCall<{ routine: Routine }>("update_routine", { id, ...fields });
      return unwrap(r).routine;
    },
    onSuccess: () => invalidate(qc),
  });
}

export function useArchiveRoutine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const r = await mcpClient.toolsCall("archive_routine", { id });
      unwrap(r);
    },
    onSuccess: () => invalidate(qc),
  });
}

export function useRestoreRoutine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const r = await mcpClient.toolsCall("restore_routine", { id });
      unwrap(r);
    },
    onSuccess: () => invalidate(qc),
  });
}

export function useDeleteRoutine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, force }: { id: string; force?: boolean }): Promise<void> => {
      const r = await mcpClient.toolsCall("delete_routine", { id, ...(force ? { force: true } : {}) });
      unwrap(r);
    },
    onSuccess: () => invalidate(qc),
  });
}

/**
 * Sprint 26.1 — compile a routine for execution. Kuvio executes nothing: this
 * returns the resolved procedure for the host (or the console's degraded path)
 * to run, then record via {@link useRecordRoutineRun} + create_activity. Throws
 * when the routine is archived or disabled.
 */
export function useRunRoutine() {
  return useMutation({
    mutationFn: async (routineId: string): Promise<CompiledRoutineProcedure> => {
      const r = await mcpClient.toolsCall<{ procedure: CompiledRoutineProcedure }>("run_routine", {
        routine_id: routineId,
      });
      return unwrap(r).procedure;
    },
  });
}

export interface RecordRoutineRunParams {
  id: string;
  summary?: string;
  outcome?: RoutineRunOutcome;
  last_run_by?: string;
  created_entity_refs?: string[];
}

export function useRecordRoutineRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...run }: RecordRoutineRunParams): Promise<Routine> => {
      const r = await mcpClient.toolsCall<{ routine: Routine }>("record_routine_run", { id, ...run });
      return unwrap(r).routine;
    },
    onSuccess: () => {
      invalidate(qc);
      // Sprint 26.1.1 — record_routine_run now writes the run's Activity row
      // server-side; refresh activity-backed surfaces (Activity tab, run history,
      // Overview "Recently") so the new run appears without a manual reload.
      qc.invalidateQueries({ queryKey: ["activities"] });
    },
  });
}

export interface PromotePromptParams {
  routine_id: string;
  prompt_name: string;
  prompt_description?: string;
}

export function usePromoteRoutinePrompt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: PromotePromptParams): Promise<{ prompt_id: string; routine: Routine }> => {
      const r = await mcpClient.toolsCall<{ prompt_id: string; routine: Routine }>(
        "promote_routine_prompt_to_shared",
        params as unknown as Record<string, unknown>,
      );
      return unwrap(r);
    },
    onSuccess: () => {
      invalidate(qc);
      qc.invalidateQueries({ queryKey: ["all-prompts"] });
      qc.invalidateQueries({ queryKey: ["prompts-in-scope"] });
    },
  });
}
